import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Grid,
  Typography, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, Box, Slide
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { putData } from "../../BaseUrl";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const GRADE_OPTIONS = [
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"
];

const SECTION_OPTIONS = ["A", "B", "C", "D"];

const VolunteerApprovalDialog = ({ open, onClose, volunteer, onSuccess }) => {
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [userLogin, setUserLogin] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [waitlistReason, setWaitlistReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!volunteer) return null;

  const handleApprove = async () => {
    if (!grade) { setError("Please select a grade"); return; }
    setLoading(true); setError("");
    try {
      await putData(`admin/volunteer-admin/${volunteer.id}/approve`, {
        assignedClass: grade,
        assignedSection: section,
        userLogin: userLogin || undefined
      });
      onSuccess("Volunteer approved and assigned successfully");
      resetFields();
    } catch (err) {
      setError(err?.response?.data?.message || "Approval failed");
    }
    setLoading(false);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { setError("Please provide a reason for rejection"); return; }
    setLoading(true); setError("");
    try {
      await putData(`admin/volunteer-admin/${volunteer.id}/reject`, { reason: rejectReason });
      onSuccess("Volunteer application rejected");
      resetFields();
    } catch (err) {
      setError(err?.response?.data?.message || "Rejection failed");
    }
    setLoading(false);
  };

  const handleWaitlist = async () => {
    setLoading(true); setError("");
    try {
      await putData(`admin/volunteer-admin/${volunteer.id}/waitlist`, {
        reason: waitlistReason || "Waitlisted"
      });
      onSuccess("Volunteer placed on waitlist");
      resetFields();
    } catch (err) {
      setError(err?.response?.data?.message || "Waitlist action failed");
    }
    setLoading(false);
  };

  const resetFields = () => {
    setGrade(""); setSection(""); setUserLogin("");
    setRejectReason(""); setWaitlistReason(""); setError("");
  };

  const handleClose = () => { resetFields(); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Transition}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Review Application</Typography>
          <Typography variant="body2" color="text.secondary">
            {volunteer.name || `${volunteer.firstName || ""} ${volunteer.lastName || ""}`} — #{volunteer.volunteerId || volunteer.id}
          </Typography>
        </Box>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>

          {/* ── Approve Panel ── */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#f9fbe7" }}>
              <Typography variant="subtitle1" fontWeight="bold" color="#2e7d32" mb={2}>
                Approve Application
              </Typography>
              <Grid container spacing={2}>
                {/* Grade */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Grade *</InputLabel>
                    <Select value={grade} label="Grade *" onChange={(e) => setGrade(e.target.value)}>
                      {GRADE_OPTIONS.map(g => (
                        <MenuItem key={g} value={g}>{g}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* Section */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Section</InputLabel>
                    <Select value={section} label="Section" onChange={(e) => setSection(e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {SECTION_OPTIONS.map(s => (
                        <MenuItem key={s} value={s}>Section {s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* User Login */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="User Login"
                    placeholder="Assign a login username..."
                    value={userLogin}
                    onChange={(e) => setUserLogin(e.target.value)}
                  />
                </Grid>
              </Grid>
              <Button variant="contained" fullWidth onClick={handleApprove} disabled={loading}
                sx={{ mt: 2, backgroundColor: "#4caf50", textTransform: "none", "&:hover": { backgroundColor: "#388e3c" } }}>
                {loading ? "Processing..." : "Approve & Assign"}
              </Button>
            </Box>
          </Grid>

          {/* ── Reject Panel ── */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fce4ec" }}>
              <Typography variant="subtitle1" fontWeight="bold" color="#c62828" mb={2}>
                Reject Application
              </Typography>
              <TextField fullWidth size="small" multiline rows={3}
                placeholder="Reason for rejection..."
                value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                sx={{ mb: 2 }} />
              <Button variant="contained" fullWidth onClick={handleReject} disabled={loading}
                sx={{ backgroundColor: "#f44336", textTransform: "none", "&:hover": { backgroundColor: "#d32f2f" } }}>
                {loading ? "Processing..." : "Reject"}
              </Button>
            </Box>
          </Grid>

          {/* ── Waitlist Panel ── */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #f48fb1", backgroundColor: "#fff0f3" }}>
              <Typography variant="subtitle1" fontWeight="bold" color="#ad1457" mb={2}>
                Waitlist Application
              </Typography>
              <TextField fullWidth size="small" multiline rows={3}
                placeholder="Reason for waitlisting (optional)..."
                value={waitlistReason} onChange={(e) => setWaitlistReason(e.target.value)}
                sx={{ mb: 2 }} />
              <Button variant="contained" fullWidth onClick={handleWaitlist} disabled={loading}
                sx={{
                  backgroundColor: "#e91e63",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#ad1457" }
                }}>
                {loading ? "Processing..." : "Add to Waitlist"}
              </Button>
            </Box>
          </Grid>

        </Grid>

        {error && <Typography color="error" variant="body2" mt={2}>{error}</Typography>}
      </DialogContent>
    </Dialog>
  );
};

export default VolunteerApprovalDialog;
